<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="hero-background">
          <div class="hero-pattern"></div>
        </div>
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <router-link to="/" class="breadcrumb-link">Home</router-link>
              <span class="breadcrumb-separator">/</span>
              <router-link to="/services" class="breadcrumb-link">Services</router-link>
              <span class="breadcrumb-separator">/</span>
              <router-link to="/liquor-license" class="breadcrumb-link">Liquor License</router-link>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Print Certificate</span>
            </nav>
            <div class="hero-title-section">
              <div class="hero-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <polyline points="6 9 6 2 18 2 18 9"></polyline>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
              </div>
              <div>
                <h1 class="page-title">Print Liquor Licence</h1>
                <p class="page-subtitle">Search and print your approved liquor licence certificate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="form-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Column - Search Form -->
            <div class="form-column">
              <div class="form-card">
                <div class="card-header">
                  <h2>Find Your Licence</h2>
                  <p>Enter your Business ID or Licence Number to search for your certificate</p>
                </div>
                <div class="card-body">
                  <!-- Search Form -->
                  <div class="search-form" v-if="!permit">
                    <div class="form-group">
                      <label class="form-label">Search By</label>
                      <div class="search-type-tabs">
                        <button
                            class="search-type-tab"
                            :class="{ active: searchType === 'businessId' }"
                            @click="searchType = 'businessId'"
                        >
                          Business ID
                        </button>
                        <button
                            class="search-type-tab"
                            :class="{ active: searchType === 'billNo' }"
                            @click="searchType = 'billNo'"
                        >
                          Bill Number
                        </button>
                        <button
                            class="search-type-tab"
                            :class="{ active: searchType === 'phone' }"
                            @click="searchType = 'phone'"
                        >
                          Phone Number
                        </button>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">{{ searchLabel }}</label>
                      <div class="search-input-wrapper">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            v-model="searchKeyword"
                            :placeholder="searchPlaceholder"
                            @keyup.enter="searchLicence"
                            class="search-input"
                        />
                        <button v-if="searchKeyword" @click="searchKeyword = ''" class="clear-btn">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <button
                        class="btn btn-primary btn-lg btn-block"
                        @click="searchLicence"
                        :disabled="!searchKeyword || searching"
                    >
                      <span v-if="!searching">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Search Licence
                      </span>
                      <span v-else class="loading-text">
                        <span class="spinner-sm"></span>
                        Searching...
                      </span>
                    </button>
                  </div>

                  <!-- Loading State -->
                  <div v-if="searching" class="loading-state">
                    <div class="loader-spinner"></div>
                    <p>Searching for your licence...</p>
                  </div>

                  <!-- No Results -->
                  <div v-if="noResults && !searching" class="no-results">
                    <div class="no-results-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="8" y1="8" x2="14" y2="14"></line>
                        <line x1="14" y1="8" x2="8" y2="14"></line>
                      </svg>
                    </div>
                    <h3>No Licence Found</h3>
                    <p>We couldn't find an approved licence matching your search. Please check your details and try again.</p>
                    <router-link to="/liquor-license" class="btn btn-outline">
                      Apply for New Licence
                    </router-link>
                  </div>

                  <!-- Permit Found - Details -->
                  <div v-if="permit" class="licence-details">
                    <div class="details-header">
                      <button class="back-link" @click="resetSearch">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="19" y1="12" x2="5" y2="12"></line>
                          <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                        New Search
                      </button>
                      <span class="status-badge" :class="permit.approved === 'true' ? 'active' : 'pending'">
                        {{ permitStatus }}
                      </span>
                    </div>

                    <div class="licence-card">
                      <div class="licence-header">
                        <div class="licence-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M8 22h8"></path>
                            <path d="M12 11v11"></path>
                            <path d="M5 3l5 7h4l5-7"></path>
                            <path d="M6.5 7h11"></path>
                          </svg>
                        </div>
                        <div class="licence-title">
                          <h3>{{ permit.businessName }}</h3>
                          <span class="licence-number">Bill No: {{ permit.billNo }}</span>
                        </div>
                      </div>

                      <div class="details-grid">
                        <div class="detail-item">
                          <label>Business ID</label>
                          <span>{{ permit.businessID }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Applicant</label>
                          <span>{{ permit.applicant || permit.fullNames }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Phone Number</label>
                          <span>{{ permit.phoneNumber || permit.ownerPhone }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Licence Category</label>
                          <span>{{ permit.licenceCategory }}</span>
                        </div>
                        <div class="detail-item full-width">
                          <label>Licence Type</label>
                          <span>{{ permit.licenceSubCategory || 'N/A' }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Sub County</label>
                          <span>{{ permit.subCountyName }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Ward</label>
                          <span>{{ permit.wardName }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Validity Period</label>
                          <span>{{ permit.licenceDuration || '12 Months' }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Fiscal Year</label>
                          <span>{{ permit.fiscalYear }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Start Date</label>
                          <span>{{ formatDate(permit.startDate) }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Expiry Date</label>
                          <span>{{ formatDate(permit.endDate) }}</span>
                        </div>
                        <div class="detail-item full-width">
                          <label>Physical Address</label>
                          <span>{{ permit.physicalAddress || 'N/A' }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Licence Fee</label>
                          <span>KES {{ formatCurrency(permit.licenceDetailAmount) }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Amount Paid</label>
                          <span>KES {{ formatCurrency(permit.licenceReceiptAmount) }}</span>
                        </div>
                        <div class="detail-item full-width" v-if="permit.license_requirements">
                          <label>Licence Conditions</label>
                          <span class="conditions-text">{{ permit.license_requirements }}</span>
                        </div>
                      </div>

                      <!-- Issuing Officer Info -->
                      <div class="officer-info" v-if="signatories && signatories.issuingOfficer">
                        <div class="officer-row">
                          <span class="officer-label">Issuing Officer:</span>
                          <span class="officer-value">{{ signatories.issuingOfficer.names }}</span>
                        </div>
                        <div class="officer-row" v-if="signatories.issuingOfficer.title">
                          <span class="officer-label">Designation:</span>
                          <span class="officer-value">{{ signatories.issuingOfficer.title }}</span>
                        </div>
                      </div>

                      <div class="licence-actions" v-if="isPrintable">
                        <button class="btn btn-primary btn-lg" @click="printCertificate">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                            <rect x="6" y="14" width="12" height="8"></rect>
                          </svg>
                          Print Certificate
                        </button>
                        <button class="btn btn-outline" @click="downloadCertificate">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          Download PDF
                        </button>
                      </div>

                      <div class="licence-warning" v-else>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <p>This licence is not yet approved for printing. Current status: <strong>{{ permitStatus }}</strong></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column - Info -->
            <div class="info-column">
              <!-- Quick Actions -->
              <div class="info-card quick-actions-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  <h3>Quick Actions</h3>
                </div>
                <div class="quick-actions-list">
                  <router-link to="/liquor-license" class="quick-action-item">
                    <div class="quick-action-icon green">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </div>
                    <div class="quick-action-content">
                      <span class="quick-action-title">New Application</span>
                      <span class="quick-action-desc">Apply for a new liquor licence</span>
                    </div>
                    <svg class="quick-action-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </router-link>
                  <router-link to="/track-liquor-application" class="quick-action-item">
                    <div class="quick-action-icon orange">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                    <div class="quick-action-content">
                      <span class="quick-action-title">Track Application</span>
                      <span class="quick-action-desc">Check your application status</span>
                    </div>
                    <svg class="quick-action-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </router-link>
                </div>
              </div>

              <!-- Help Card -->
              <div class="info-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <h3>Need Help?</h3>
                </div>
                <p class="help-text">Can't find your licence? Make sure you have an approved application first.</p>
                <router-link to="/contact" class="help-link">
                  Contact Support
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </router-link>
              </div>

              <!-- Info Card -->
              <div class="info-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <h3>Important Note</h3>
                </div>
                <ul class="note-list">
                  <li>Only approved licences can be printed</li>
                  <li>Ensure your licence is not expired</li>
                  <li>Print on A4 paper for best results</li>
                  <li>Keep your licence displayed at premises</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { execute, liquor } from "@/api";

export default {
  name: "PrintLiquorLicense",
  components: {
    HeaderComponent,
    FooterComponent
  },
  props: {
    businessID: {
      type: String,
      default: null
    },
    billNo: {
      type: String,
      default: null
    },
    phone: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      searchType: 'businessId',
      searchKeyword: '',
      searching: false,
      noResults: false,
      permit: null,
      county: null,
      signatories: null
    };
  },
  computed: {
    searchLabel() {
      switch (this.searchType) {
        case 'businessId': return 'Business ID';
        case 'billNo': return 'Bill Number';
        case 'phone': return 'Phone Number';
        default: return 'Search';
      }
    },
    searchPlaceholder() {
      switch (this.searchType) {
        case 'businessId': return 'Enter your Business ID (e.g. 910)';
        case 'billNo': return 'Enter your Bill Number (e.g. BLL...)';
        case 'phone': return 'Enter phone number (e.g. 0712345678)';
        default: return 'Enter search keyword...';
      }
    },
    isPrintable() {
      return this.permit && this.permit.printable === 'true' && this.permit.approved === 'true';
    },
    permitStatus() {
      if (!this.permit) return '';
      if (this.permit.approved === 'true') return 'APPROVED';
      return this.permit.stageStatus || this.permit.status || 'PENDING';
    }
  },
  mounted() {
    // Check for URL parameters and auto-search
    this.checkUrlParams();
  },
  methods: {
    checkUrlParams() {
      // Check props first (from router)
      if (this.businessID) {
        this.searchType = 'businessId';
        this.searchKeyword = this.businessID;
        this.searchLicence();
        return;
      }

      if (this.billNo) {
        this.searchType = 'billNo';
        this.searchKeyword = this.billNo;
        this.searchLicence();
        return;
      }

      if (this.phone) {
        this.searchType = 'phone';
        this.searchKeyword = this.phone;
        this.searchLicence();
        return;
      }

      // Also check query params directly as fallback
      const query = this.$route.query;
      if (query.businessID || query.businessId || query.id) {
        this.searchType = 'businessId';
        this.searchKeyword = query.businessID || query.businessId || query.id;
        this.searchLicence();
        return;
      }

      if (query.billNo || query.bill) {
        this.searchType = 'billNo';
        this.searchKeyword = query.billNo || query.bill;
        this.searchLicence();
        return;
      }

      if (query.phone || query.phoneNumber) {
        this.searchType = 'phone';
        this.searchKeyword = query.phone || query.phoneNumber;
        this.searchLicence();
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    searchLicence() {
      if (!this.searchKeyword) return;

      this.searching = true;
      this.noResults = false;
      this.permit = null;
      this.county = null;
      this.signatories = null;

      const data = new FormData();
      data.append("function", "printLiquorPermit");

      // Send the appropriate parameter based on search type
      const keyword = this.searchKeyword.trim();

      if (this.searchType === 'businessId') {
        data.append("businessID", keyword);
      } else if (this.searchType === 'billNo') {
        data.append("billNo", keyword);
      } else if (this.searchType === 'phone') {
        // phoneNumber will search both phoneNumber and ownerPhone on backend
        data.append("phoneNumber", keyword);
      }

      execute(data, liquor)
          .then((res) => {
            this.searching = false;
            if (res.data.success && res.data.data.permit) {
              this.permit = res.data.data.permit;
              this.county = res.data.data.county;
              this.signatories = res.data.data.signatories;
            } else {
              this.noResults = true;
            }
          })
          .catch((e) => {
            this.searching = false;
            this.noResults = true;
            console.error(e.message);
          });
    },

    resetSearch() {
      this.permit = null;
      this.county = null;
      this.signatories = null;
      this.noResults = false;
    },

    printCertificate() {
      if (!this.permit) return;

      localStorage['params'] = JSON.stringify({
        id: this.permit.id,
        businessID: this.permit.businessID,
        billNo: this.permit.billNo
      });
      const routeData = this.$router.resolve({ name: 'liquor-certificate' });
      window.open(routeData.href, '_blank');
    },

    downloadCertificate() {
      // Same as print - opens in new tab
      this.printCertificate();
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
  position: relative;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.hero-pattern {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.hero-title-section {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.hero-icon {
  width: 64px;
  height: 64px;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 4px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Form Section */
.form-section {
  padding: 48px 0;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 32px;
  align-items: start;
}

/* Form Card */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.card-header {
  padding: 24px 32px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.card-header p {
  color: #64748b;
  font-size: 14px;
}

.card-body {
  padding: 32px;
}

/* Search Form */
.search-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.search-type-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.search-type-tab {
  padding: 10px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.search-type-tab:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.search-type-tab.active {
  background: #0f4c81;
  border-color: #0f4c81;
  color: white;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-wrapper > svg {
  position: absolute;
  left: 16px;
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 3px rgba(15, 76, 129, 0.1);
}

.clear-btn {
  position: absolute;
  right: 12px;
  padding: 8px;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(15, 76, 129, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline {
  background: white;
  border: 2px solid #e2e8f0;
  color: #1e293b;
}

.btn-outline:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.btn-lg {
  padding: 16px 32px;
  font-size: 16px;
}

.btn-block {
  width: 100%;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 48px 24px;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #0f4c81;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 48px 24px;
}

.no-results-icon {
  width: 80px;
  height: 80px;
  background: #fee2e2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #dc2626;
}

.no-results h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-results p {
  color: #64748b;
  margin-bottom: 24px;
}

/* Search Results */
.search-results {
  margin-top: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.results-count {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.results-list {
  max-height: 400px;
  overflow-y: auto;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.2s;
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover {
  background: #f8fafc;
}

.result-icon {
  width: 48px;
  height: 48px;
  background: #dbeafe;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
}

.result-icon.green {
  background: #d1fae5;
  color: #059669;
}

.result-icon.orange {
  background: #ffedd5;
  color: #ea580c;
}

.result-content {
  flex: 1;
}

.result-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.result-content p {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}

.result-tag {
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
  color: #64748b;
}

.result-status {
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
}

.result-status.active {
  background: #d1fae5;
  color: #059669;
}

.result-status.pending {
  background: #fef3c7;
  color: #d97706;
}

.result-action {
  color: #94a3b8;
}

/* Licence Details */
.licence-details {
  margin-top: 24px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
}

.back-link:hover {
  color: #0f4c81;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: #d1fae5;
  color: #059669;
}

.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}

.licence-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.licence-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  color: white;
}

.licence-icon {
  width: 56px;
  height: 56px;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.licence-title h3 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.licence-number {
  font-size: 14px;
  opacity: 0.9;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: #e2e8f0;
}

.detail-item {
  padding: 16px 20px;
  background: white;
}

.detail-item.full-width {
  grid-column: span 2;
}

.detail-item label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
}

.licence-actions {
  padding: 20px;
  background: #f8fafc;
  display: flex;
  gap: 12px;
}

.licence-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #fef3c7;
  color: #92400e;
}

.licence-warning svg {
  flex-shrink: 0;
}

.licence-warning p {
  font-size: 14px;
}

/* Officer Info */
.officer-info {
  padding: 16px 20px;
  background: #eff6ff;
  border-top: 1px solid #e2e8f0;
}

.officer-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.officer-row:last-child {
  margin-bottom: 0;
}

.officer-label {
  font-size: 13px;
  color: #64748b;
  min-width: 120px;
}

.officer-value {
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
}

.conditions-text {
  font-size: 13px !important;
  line-height: 1.6;
  color: #475569 !important;
}

/* Info Column */
.info-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.quick-actions-card {
  border: 2px solid #fef3c7;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  color: #0f4c81;
}

.info-card-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.quick-actions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-action-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s;
}

.quick-action-item:hover {
  border-color: #0f4c81;
  box-shadow: 0 4px 12px rgba(15, 76, 129, 0.1);
  transform: translateX(4px);
}

.quick-action-icon {
  width: 48px;
  height: 48px;
  background: #dbeafe;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  flex-shrink: 0;
}

.quick-action-icon.orange {
  background: #ffedd5;
  color: #ea580c;
}

.quick-action-icon.green {
  background: #d1fae5;
  color: #059669;
}

.quick-action-content {
  flex: 1;
  min-width: 0;
}

.quick-action-title {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.quick-action-desc {
  display: block;
  font-size: 12px;
  color: #64748b;
}

.quick-action-arrow {
  color: #94a3b8;
  flex-shrink: 0;
  transition: transform 0.2s, color 0.2s;
}

.quick-action-item:hover .quick-action-arrow {
  color: #0f4c81;
  transform: translateX(4px);
}

.help-text {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
}

.help-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0f4c81;
  font-weight: 600;
  text-decoration: none;
  font-size: 14px;
}

.help-link:hover {
  gap: 12px;
}

.note-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.note-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  font-size: 13px;
  color: #475569;
  border-bottom: 1px solid #f1f5f9;
}

.note-list li:last-child {
  border-bottom: none;
}

.note-list li::before {
  content: "•";
  color: #0f4c81;
  font-weight: bold;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-column {
    order: -1;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .info-card {
    flex: 1;
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .hero-title-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .search-type-tabs {
    flex-direction: column;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .detail-item.full-width {
    grid-column: span 1;
  }

  .licence-actions {
    flex-direction: column;
  }

  .info-column {
    flex-direction: column;
  }

  .info-card {
    min-width: 100%;
  }
}
</style>